import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack,
  Pagination,
} from "@mui/material";

const ITEMS_PER_PAGE = 5;

const ActivityTimeline = ({ activity = [] }) => {
  const [range, setRange] = useState("week");
  const [page, setPage] = useState(1);

  const filteredActivity = useMemo(() => {
    if (!Array.isArray(activity)) return [];

    const sorted = [...activity].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );

    if (range === "all") return sorted;

    const now = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);

    return sorted.filter((item) => {
      const itemDate = new Date(item.date);
      return itemDate >= sevenDaysAgo && itemDate <= now;
    });
  }, [activity, range]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredActivity.length / ITEMS_PER_PAGE)
  );

  const paginatedActivity = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredActivity.slice(start, end);
  }, [filteredActivity, page]);

  useEffect(() => {
    setPage(1);
  }, [range, activity]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleRangeChange = (_, value) => {
    if (value) setRange(value);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.25,
            }}
          >
            Recent Activity
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
            }}
          >
            Timeline of recent member activity.
          </Typography>
        </Box>

        <ToggleButtonGroup
          size="small"
          value={range}
          exclusive
          onChange={handleRangeChange}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              px: 1.5,
              borderColor: "rgba(47,99,173,0.18)",
            },
            "& .Mui-selected": {
              backgroundColor: "rgba(47,99,173,0.08) !important",
              color: "#2f63ad !important",
            },
          }}
        >
          <ToggleButton value="week">Last 7 Days</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {filteredActivity.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            borderRadius: "12px",
            border: "1px dashed rgba(47,99,173,0.18)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            {range === "week"
              ? "No activity in the last 7 days."
              : "No activity available."}
          </Typography>
        </Box>
      ) : (
        <>
          <Stack spacing={1.25}>
            {paginatedActivity.map((item, index) => (
              <Box
                key={`${item.date}-${index}`}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "flex-start",
                  p: 1.5,
                  borderRadius: "12px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <Box
                  sx={{
                    minWidth: 82,
                    display: "flex",
                    justifyContent: "center",
                    pt: 0.25,
                  }}
                >
                  <Chip
                    size="small"
                    label={new Date(item.date).toLocaleDateString("en-US", {
                      day: "2-digit",
                      month: "short",
                    })}
                    sx={{
                      fontWeight: 700,
                      backgroundColor: "rgba(47,99,173,0.10)",
                      color: "#2f63ad",
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    pl: 1.5,
                    borderLeft: "3px solid #2f63ad",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#223142",
                      fontWeight: 600,
                      lineHeight: 1.45,
                    }}
                  >
                    {item.message}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: "#8a94a6",
                      mt: 0.5,
                      display: "block",
                    }}
                  >
                    {new Date(item.date).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          {filteredActivity.length > ITEMS_PER_PAGE && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2.5,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                size="small"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ActivityTimeline;

